import {
  AddOnData,
  ComponentQuantityData,
  DisplayContext,
  PartialPricingEngineOverrides,
} from '@frontend/api';

import {
  getAddOn as getAddOnFromStore,
  getCellsForAddOn,
} from 'modules/quotes/quoteItems/data/selectors';

import { PAGE_REQUEST } from 'store/app/types';
import { RootState } from 'store/configureStore';
import { ActionWithPayload } from 'store/types';

import { types } from './actions';

type AddOnModalState = {
  addOnId: number | null;
  cells: ComponentQuantityData[];
  displayContext: DisplayContext[];
  isReadOnly: boolean;
  isOpen: boolean;
  overrides: PartialPricingEngineOverrides;
  liveAddOn: AddOnData | null;
};

const initialState: AddOnModalState = {
  addOnId: null,
  cells: [],
  displayContext: [],
  isReadOnly: false,
  isOpen: false,
  overrides: {},
  liveAddOn: null,
};

const reducer = (
  state = initialState,
  action: ActionWithPayload<string, any>,
) => {
  const { payload } = action;
  switch (action.type) {
    case PAGE_REQUEST:
      return {
        ...initialState,
      };
    case types.OPEN_ADD_ON_MODAL:
      return {
        ...state,
        addOnId: payload.addOn.id,
        cells: payload.cells,
        displayContext: payload.addOn.displayContext,
        isOpen: true,
        isReadOnly: payload.isReadOnly,
        overrides: payload.addOn.overrides,
      };
    case types.CLOSE_ADD_ON_MODAL:
      return {
        ...state,
        addOnId: null,
        cells: [],
        displayContext: [],
        isOpen: false,
        isReadOnly: false,
        overrides: {},
        liveAddOn: null,
      };
    case types.LIVE_ADD_ON_PREVIEW_REQUEST:
      return {
        ...state,
        overrides: payload.overrides,
      };
    case types.LIVE_ADD_ON_PREVIEW_SUCCESS:
      return {
        ...state,
        cells: payload.addOnCells,
        displayContext: payload.displayContext,
        liveAddOn: payload,
      };
    case types.RECEIVE_OVERRIDES:
      return {
        ...state,
        overrides: payload.nextOverrides,
      };
    default:
      return state;
  }
};

export default reducer;

const getScope = (state: RootState): AddOnModalState =>
  state.modules.quotes.addOnModal;

export const getAddOnId = (state: RootState) => getScope(state).addOnId;

export const getAddOn = (state: RootState) => {
  const addOnId = getAddOnId(state);
  return addOnId ? getAddOnFromStore(state, addOnId) : undefined;
};

export const getCurrentOverrides = (state: RootState) =>
  getScope(state).overrides;

export const getDisplayContext = (state: RootState) =>
  getScope(state).displayContext;

export const getGlobalAddOnCells = (state: RootState) => {
  const addOnId = getAddOnId(state);
  return addOnId ? getCellsForAddOn(state, addOnId) : [];
};

export const getIsAddOnModalOpen = (state: RootState) => getScope(state).isOpen;

export const getIsReadOnly = (state: RootState) => getScope(state).isReadOnly;

export const getLocalAddOnCells = (state: RootState) => getScope(state).cells;

export const getLiveAddOn = (state: RootState) => getScope(state).liveAddOn;
